/**
 * Price Range interaction logic, co-located with the tool (Phase 5). Pure
 * helpers the chart's shared mouse handlers call — placement build, drag-time
 * field updates, the live preview, and the per-drawing menu. Keeping them here
 * (next to the renderer + settings) instead of inline in Chart.tsx is the
 * interaction half of "a tool is one module".
 */
import { type PriceRangeBox } from "@/state/slices/uiSlice";
import type { AppDispatch } from "@/state/store";
import type { DragTarget, MenuItem } from "@/components/Chart/types";
import { drawingMenuItems } from "@/components/Chart/drawingMenu";

type Point = { time: number; price: number };

/** Build a box from two anchors (A = start, B = end). Click-click with no time
 *  separation gets a default forward span so the box has grabbable width. */
export function buildPriceRangeBox(a: Point, b: Point, tfSec: number): PriceRangeBox {
  const endTime = b.time !== a.time ? b.time : a.time + 20 * tfSec;
  return {
    id: `pr_${Date.now()}`,
    startTime: a.time,
    startPrice: a.price,
    endTime,
    endPrice: b.price,
  };
}

/** Live preview box while placing (press-drag, or between click-1 and 2). */
export function priceRangePreview(anchor: Point, hover: Point): PriceRangeBox {
  return {
    id: "preview",
    startTime: anchor.time,
    startPrice: anchor.price,
    endTime: hover.time,
    endPrice: hover.price,
  };
}

/** Field update for a drag tick. `body` translates both anchors by the
 *  cursor-minus-mousedown delta; `start`/`end` move their own corner. Returns
 *  null when there's nothing to apply. */
export function priceRangeDragPartial(
  drag: Extract<DragTarget, { type: "priceRange"; anchor: "start" | "end" }>,
  time: number | null,
  price: number | null,
): Partial<Omit<PriceRangeBox, "id">> | null {
  const partial: Partial<Omit<PriceRangeBox, "id">> = {};
  if (time != null) {
    if (drag.anchor === "start") partial.startTime = time;
    else partial.endTime = time;
  }
  if (price != null) {
    if (drag.anchor === "start") partial.startPrice = price;
    else partial.endPrice = price;
  }
  return Object.keys(partial).length > 0 ? partial : null;
}

/** Per-box right-click menu — standardized Settings + Delete. */
export function priceRangeMenuItems(
  dispatch: AppDispatch,
  tabId: string,
  id: string,
): MenuItem[] {
  return drawingMenuItems(dispatch, tabId, "priceRange", id);
}
